import { HTMLAttributes } from 'react';
import { cn } from '../utils';
import {
  safeMindsLogoFull,
  safeMindsLogoFullAlt,
  safeMindsLogoFullDark,
  safeMindsLogoFullLight,
  safeMindsLogoWithoutText,
} from '../assets';

type SafeMindsLogoVariant =
  | 'mark'
  | 'full'
  | 'full-alt'
  | 'full-dark'
  | 'full-light'
  | 'mark-only';

const VARIANT_CONFIG: Record<
  SafeMindsLogoVariant,
  { src: string; defaultWidth: number; ratio: number; hasEmbeddedText: boolean }
> = {
  full: {
    src: safeMindsLogoFull,
    defaultWidth: 180,
    ratio: 1024 / 1536,
    hasEmbeddedText: true,
  },
  'full-alt': {
    src: safeMindsLogoFullAlt,
    defaultWidth: 180,
    ratio: 1024 / 1536,
    hasEmbeddedText: true,
  },
  'full-dark': {
    src: safeMindsLogoFullDark,
    defaultWidth: 220,
    ratio: 354 / 866,
    hasEmbeddedText: true,
  },
  'full-light': {
    src: safeMindsLogoFullLight,
    defaultWidth: 220,
    ratio: 354 / 866,
    hasEmbeddedText: true,
  },
  'mark-only': {
    src: safeMindsLogoWithoutText,
    defaultWidth: 72,
    ratio: 1,
    hasEmbeddedText: false,
  },
  mark: {
    src: safeMindsLogoWithoutText,
    defaultWidth: 68,
    ratio: 1,
    hasEmbeddedText: false,
  },
};

export interface SafeMindsLogoProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Backwards compatible alias for `width`.
   */
  size?: number;
  /**
   * Explicit width of the asset. Height is derived automatically unless `height` is provided.
   */
  width?: number;
  /**
   * Target height of the asset. When provided, width is derived automatically (unless `width` is also passed).
   */
  height?: number;
  variant?: SafeMindsLogoVariant;
  /**
   * Optional text label rendered to the right of the crest for the "mark" variant.
   * Ignored for variants that already contain text.
   */
  withText?: boolean;
  label?: string;
  /**
   * Reduce spacing around the crest/label for tighter layouts.
   */
  compact?: boolean;
}

export function SafeMindsLogo({
  size,
  width,
  height,
  variant = 'full',
  withText,
  label = 'SafeMindsAI',
  compact = false,
  className,
  ...props
}: SafeMindsLogoProps) {
  const config = VARIANT_CONFIG[variant];
  let computedWidth = width ?? size ?? config.defaultWidth;
  let computedHeight = Math.round(computedWidth * config.ratio);

  if (height) {
    computedHeight = height;
    computedWidth = Math.round(height / config.ratio);
  }

  const shouldRenderLabel =
    Boolean(withText ?? variant === 'mark') && !config.hasEmbeddedText;

  return (
    <div
      className={cn(
        'inline-flex items-center gap-3 text-primary leading-none',
        compact && 'gap-2',
        className
      )}
      {...props}
    >
      <img
        src={config.src}
        width={computedWidth}
        height={computedHeight}
        alt="SafeMindsAI brand mark"
        loading="lazy"
        className="block max-h-full object-contain drop-shadow-[0_8px_24px_rgba(63,125,201,0.35)]"
      />
      {shouldRenderLabel ? (
        <span className="text-base font-semibold tracking-tight text-[color:#0F265A]">
          {label}
        </span>
      ) : null}
    </div>
  );
}
